import React from 'react';
import { Tag } from 'antd';
import type { Product } from '../../types/product';

export const levelMeta: Record<string, { label: string; color: string; status: 'success' | 'normal' | 'exception' }> = {
  high: { label: '高度重复', color: 'red', status: 'exception' },
  medium: { label: '疑似重复', color: 'orange', status: 'normal' },
  low: { label: '低相似', color: 'blue', status: 'normal' },
};

export const statusLabels: Record<string, string> = {
  candidate: '待处理',
  confirmed: '已确认隐藏',
  ignored: '已忽略',
  none: '普通',
};

export const sourceLabels: Record<string, string> = {
  haoyi: '号易',
  '172': '172平台',
  manual: '手动',
};

export const RANGE_DIFFERENT_FIELD = '可办地区范围';

export const hasRangeDifferentShippingScope = (differentFields?: string[] | null) => (
  differentFields || []
).includes(RANGE_DIFFERENT_FIELD);

const formatMoney = (value?: number | null) => (value ? `${value}元` : '-');
const formatBoolean = (value?: boolean) => (value ? '是' : '否');

const formatShippingScope = (product: Product) => {
  if (product.shippingPolicy?.display) return product.shippingPolicy.display;
  if (product.derivedShippingScope?.display) return product.derivedShippingScope.display;

  const regions = Array.isArray(product.forbidden_regions) ? product.forbidden_regions.join('、') : '';
  if (!regions) return '全国/未限制';
  if (product.shipping_region_type === 'whitelist') return `仅发：${regions}`;
  if (product.shipping_region_type === 'blacklist') return `不发：${regions}`;
  return regions;
};

export interface CompareRow {
  key: string;
  field: string;
  values: Record<number, React.ReactNode>;
}

export const buildCompareRows = (products: Product[]): CompareRow[] => {
  const row = (key: string, field: string, render: (product: Product) => React.ReactNode): CompareRow => ({
    key,
    field,
    values: Object.fromEntries(products.map((product) => [product.id, render(product)])),
  });

  return [
    row('source', '来源平台', (product) => sourceLabels[product.source_platform || ''] || product.source_platform || '-'),
    row('name', '产品名称', (product) => product.name || '-'),
    row('operator', '运营商', (product) => product.operator || '-'),
    row('monthly_current', '当前月租', (product) => formatMoney(product.monthly_current)),
    row('traffic_general', '通用流量', (product) => product.traffic_general || '-'),
    row('traffic_directional', '定向流量', (product) => product.traffic_directional || '-'),
    row('voice', '通话时长', (product) => product.voice || '-'),
    row('age', '年龄范围', (product) => (
      product.age_min != null && product.age_max != null ? `${product.age_min}~${product.age_max}岁` : '-'
    )),
    row('shipping_scope', '可办地区', (product) => formatShippingScope(product)),
    row('benefits', '权益/会员', (product) => product.derivedBenefitDisplay || '无'),
    row('settle_price', '结算价', (product) => (product.settle_price ? `¥${product.settle_price}` : '-')),
    row('status', '状态', (product) => `${product.status}/${product.external_status}`),
    row('shipping', '需要发货', (product) => formatBoolean(product.need_shipping)),
    row('id_card', '身份证要求', (product) => (
      [
        product.need_id_card && '身份证号',
        product.need_id_card_pic && '身份证图片',
        product.need_face_pic && '人像照',
        product.need_verify_code && '验证码',
      ].filter(Boolean).join('、') || '无'
    )),
    row('client_visible', '客户端展示', (product) => (
      <Tag color={product.is_hidden_from_client ? 'red' : 'green'}>
        {product.is_hidden_from_client ? '隐藏' : '可见'}
      </Tag>
    )),
  ];
};
